/**
 * Programme Dates Section for Project Detail
 * Shows Start Date, Duration (weeks), and Completion Date with two-way calculation
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  ChevronDown, 
  ChevronUp, 
  Edit, 
  Save, 
  X, 
  Loader2,
  Calendar,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, addWeeks, differenceInWeeks } from 'date-fns';

export function ProjectProgrammeSection({ 
  project,
  api,
  canEdit,
  onUpdate
}) {
  const [expanded, setExpanded] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form values
  const [startDate, setStartDate] = useState('');
  const [duration, setDuration] = useState('');
  const [completionDate, setCompletionDate] = useState('');
  const [notes, setNotes] = useState('');
  
  // Track which field changed last for two-way calculation
  const [lastChanged, setLastChanged] = useState(null);

  // Initialize form values from project data
  useEffect(() => {
    if (project?.programme_dates) {
      const pd = project.programme_dates;
      setStartDate(pd.proposed_start_date || '');
      setDuration(pd.estimated_duration_weeks?.toString() || '');
      setCompletionDate(pd.proposed_completion_date || '');
      setNotes(pd.notes || '');
    } else {
      setStartDate('');
      setDuration('');
      setCompletionDate('');
      setNotes('');
    }
  }, [project]);

  // Two-way calculation logic
  useEffect(() => {
    if (!editMode) return;
    
    if (lastChanged === 'startDate' || lastChanged === 'duration') {
      // Calculate completion date from start + duration
      if (startDate && duration) {
        try {
          const start = parseISO(startDate);
          const weeks = parseInt(duration, 10);
          if (!isNaN(weeks) && weeks > 0) {
            const completion = addWeeks(start, weeks);
            setCompletionDate(format(completion, 'yyyy-MM-dd'));
          }
        } catch (e) {
          // Invalid date, ignore
        }
      }
    } else if (lastChanged === 'completionDate') {
      // Calculate duration from start and completion
      if (startDate && completionDate) {
        try {
          const start = parseISO(startDate);
          const completion = parseISO(completionDate);
          const weeks = differenceInWeeks(completion, start);
          if (weeks >= 0) {
            setDuration(Math.ceil(weeks).toString());
          }
        } catch (e) {
          // Invalid date, ignore
        }
      }
    }
  }, [startDate, duration, completionDate, lastChanged, editMode]);

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    setLastChanged('startDate');
  };

  const handleDurationChange = (e) => {
    setDuration(e.target.value);
    setLastChanged('duration');
  };

  const handleCompletionDateChange = (e) => {
    setCompletionDate(e.target.value);
    setLastChanged('completionDate');
  };

  const startEdit = () => {
    setEditMode(true);
    setLastChanged(null);
  };

  const cancelEdit = () => {
    // Reset to original values
    if (project?.programme_dates) {
      const pd = project.programme_dates;
      setStartDate(pd.proposed_start_date || '');
      setDuration(pd.estimated_duration_weeks?.toString() || '');
      setCompletionDate(pd.proposed_completion_date || '');
      setNotes(pd.notes || '');
    } else {
      setStartDate('');
      setDuration('');
      setCompletionDate('');
      setNotes('');
    }
    setEditMode(false);
    setLastChanged(null);
  };

  const handleSave = async () => {
    setSubmitting(true);
    try {
      await api.put(`/projects/${project.id}`, {
        programme_dates: {
          proposed_start_date: startDate || null,
          estimated_duration_weeks: duration ? parseInt(duration, 10) : null,
          proposed_completion_date: completionDate || null,
          notes: notes || null
        }
      });
      
      toast.success('Programme dates updated');
      setEditMode(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update programme dates');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  const programmeDates = project?.programme_dates || {};
  const hasLinkedTender = !!project?.source_tender_id;

  return (
    <Card className="bg-[#2A3240] border-white/10">
      <CardHeader 
        className="pb-3 cursor-pointer hover:bg-white/5 transition-colors rounded-t-lg"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2 text-lg">
            <Calendar className="w-5 h-5 text-[#F5A623]" />
            Programme
            {hasLinkedTender && programmeDates.proposed_start_date && (
              <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs ml-2">
                From Tender
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {programmeDates.estimated_duration_weeks && (
              <Badge className="bg-[#F5A623]/20 text-[#F5A623] border-[#F5A623]/30">
                {programmeDates.estimated_duration_weeks} weeks
              </Badge>
            )}
            {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </div>
        </div>
      </CardHeader>
      
      {expanded && (
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <p className="text-gray-400 text-sm">
              {editMode 
                ? 'Enter Start Date + Duration to auto-calculate Completion, or enter Completion directly to recalculate Duration.'
                : 'Project timeline and milestone dates.'
              }
            </p>
            {canEdit && !editMode && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); startEdit(); }}
                className="text-gray-400 hover:text-[#F5A623] h-7"
                data-testid="edit-programme-btn"
              >
                <Edit className="w-3 h-3 mr-1" />
                Edit
              </Button>
            )}
            {editMode && (
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={cancelEdit}
                  className="text-gray-400 hover:text-white h-7"
                >
                  <X className="w-3 h-3 mr-1" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={submitting}
                  className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] h-7"
                  data-testid="save-programme-btn"
                >
                  {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Save className="w-3 h-3 mr-1" />Save</>}
                </Button>
              </div>
            )}
          </div>

          {editMode ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-400 text-xs mb-1.5 block">Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  className="bg-[#1E2530] border-white/20 text-white"
                  data-testid="programme-start-date"
                />
              </div>
              <div>
                <Label className="text-gray-400 text-xs mb-1.5 block">Duration (weeks)</Label>
                <Input
                  type="number"
                  min="1"
                  value={duration}
                  onChange={handleDurationChange}
                  placeholder="12"
                  className="bg-[#1E2530] border-white/20 text-white"
                  data-testid="programme-duration"
                />
              </div>
              <div>
                <Label className="text-gray-400 text-xs mb-1.5 block">Completion Date</Label>
                <Input
                  type="date"
                  value={completionDate}
                  onChange={handleCompletionDateChange}
                  className="bg-[#1E2530] border-white/20 text-white"
                  data-testid="programme-completion-date"
                />
              </div>
              <div className="md:col-span-3">
                <Label className="text-gray-400 text-xs mb-1.5 block">Notes</Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Programme notes..."
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              <div className="p-3 rounded-lg bg-[#1E2530] border border-white/10">
                <p className="text-xs text-gray-500 mb-1">Start Date</p>
                <p className="text-white font-medium">
                  {formatDisplayDate(programmeDates.proposed_start_date)}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-[#1E2530] border border-white/10">
                <p className="text-xs text-gray-500 mb-1">Duration</p>
                <p className="text-white font-medium flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-[#F5A623]" />
                  {programmeDates.estimated_duration_weeks ? `${programmeDates.estimated_duration_weeks} weeks` : '—'}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-[#1E2530] border border-white/10">
                <p className="text-xs text-gray-500 mb-1">Completion Date</p>
                <p className="text-white font-medium">
                  {formatDisplayDate(programmeDates.proposed_completion_date)}
                </p>
              </div>
              {programmeDates.notes && (
                <div className="col-span-3 p-3 rounded-lg bg-[#1E2530] border border-white/10">
                  <p className="text-xs text-gray-500 mb-1">Notes</p>
                  <p className="text-white text-sm">{programmeDates.notes}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
